/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// formik components
import { ErrorMessage, Field } from "formik";

// Material Dashboard 2 PRO React TS components
import MDBox from "dashboard-template/components/MDBox";
import MDTypography from "dashboard-template/components/MDTypography";
import { forwardRef, useEffect, useMemo, useState } from "react";
import { FormInputProps } from "./FormInput";
import {
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Input,
  InputProps,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import DialogModal from "components/ui/DialogModal";

import { useTranslation } from "react-i18next";

// Declaring props types for FormField
type FormPassInputProps = FormInputProps & {
  showModal?: boolean;
  modalBody?: React.ReactNode;
  modalTitle?: string;
  forceHidePassword?: boolean;
};

type PassInputProps = InputProps & {
  handleClickShowPassword: () => void;
  handleMouseDownPassword: (event: React.MouseEvent<HTMLButtonElement>) => void;
  showPassword: boolean;
  label: string;
};

const InputPassword = forwardRef(
  (
    {
      handleClickShowPassword,
      handleMouseDownPassword,
      showPassword,
      label,
      ...rest
    }: PassInputProps,
    ref
  ) => (
    <FormControl className="FormControl-content" variant="standard">
      <InputLabel htmlFor="outlined-adornment-password">{label}</InputLabel>
      <Input
        type={showPassword ? "text" : "password"}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={handleClickShowPassword}
              onMouseDown={handleMouseDownPassword}
              edge="end"
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        }
        ref={ref}
        {...rest}
      />
    </FormControl>
  )
);

const FormInputPassword = forwardRef(
  (
    {
      name,
      showModal: showModalFlag,
      modalBody,
      modalTitle,
      forceHidePassword,
      ...rest
    }: FormPassInputProps,
    ref
  ) => {
    const [showPassword, setShowPassword] = useState<boolean>(!forceHidePassword);
    const [showModal, setShowModal] = useState<boolean>(false);
    const inputType = useMemo<string>(() => (showPassword ? "text" : "password"), [showPassword]);

    const handleClickShowPassword = () => {
      if (showModalFlag && !showPassword) {
        setShowModal(true);
      } else {
        setShowPassword((show) => !show);
      }
    };
    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
    };
    const { t } = useTranslation();

    useEffect(() => {
      if (forceHidePassword) {
        setShowPassword(false);
      }
    }, [forceHidePassword]);

    return (
      <MDBox mb={1.5}>
        <Field
          className={`formik-input ${rest.className}`}
          name={name}
          as={InputPassword}
          handleClickShowPassword={handleClickShowPassword}
          handleMouseDownPassword={handleMouseDownPassword}
          showPassword={showPassword}
          fullWidth
          ref={ref}
          type={inputType}
          {...rest}
          variant="standard"
        />
        <MDBox mt={0.75}>
          <MDTypography component="div" variant="caption" color="error" fontWeight="regular">
            {(<ErrorMessage name={name} />) as any}
          </MDTypography>
        </MDBox>
        <DialogModal
          open={showModal}
          title={modalTitle}
          closeButtonLabel={t("formImputPasword.cancel")}
          okButtonLabel={t("formImputPasword.show")}
          onClose={() => setShowModal(false)}
          onOK={() => {
            setShowModal(false);
            setShowPassword(true);
          }}
        >
          <>{modalBody}</>
        </DialogModal>
      </MDBox>
    );
  }
);

export default FormInputPassword;
